import { z } from 'zod';

/**
 * StockLot schemas — mirror the backend CreateStockLotDto / UpdateStockLotDto.
 *
 * The backend rejects post-creation mutation of partId, vesselId, and
 * location, because changing any of those would either invalidate the
 * movement history (different part) or change the meaning of the lot
 * (different vessel = different scope altogether). To move stock to
 * another location, the operator creates a new lot and runs a
 * transfer movement.
 *
 *   stockLotCreateSchema — full payload, used by the create form.
 *   stockLotUpdateSchema — editable subset (thresholds + notes).
 */

const optionalNonNegativeInt = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  })
  .refine((v) => v === null || v >= 0, {
    message: 'Must be a non-negative integer',
  });

const optionalString = (max: number) =>
  z
    .string()
    .max(max)
    .optional()
    .transform((v) => (v === undefined || v === '' ? null : v));

/** Fields editable in BOTH create and update. */
const editableFields = {
  minStockOverride: optionalNonNegativeInt,
  maxStockOverride: optionalNonNegativeInt,
  notes: optionalString(2000),
};

export const stockLotCreateSchema = z.object({
  ...editableFields,
  partId: z.string().uuid(),
  vesselId: z.string().uuid(),
  location: z.string().min(1).max(100),
});

export type StockLotCreateValues = z.infer<typeof stockLotCreateSchema>;

export const stockLotUpdateSchema = z.object({
  ...editableFields,
});

export type StockLotUpdateValues = z.infer<typeof stockLotUpdateSchema>;
